/**
 * Which backend, and WHY.
 *
 * ⛔ THE REASON IS NOT OPTIONAL. `[backend].default = "auto"` is the shipped default, and config.md
 * §3 requires the resolution banner to print the backend *and why*. A tool that silently picks a
 * brain leaves a user unable to explain a bad review, and the commonest cause here is mundane and
 * invisible: a background process cannot read the login keychain, so the CLI is present, installed,
 * and unauthenticated, and the run quietly goes to the metered path.
 *
 * ⛔ "PROBE FAILED" IS NOT "BAD CREDENTIAL". They are different states with different fixes, and
 * conflating them sends a user to rotate a working key. Measured and recorded in config.md §3: the
 * `claude` CLI prints "Not logged in" on stdout and EXITS 0, so the exit code carries no auth
 * information in either direction — not success, not failure.
 */

import type { SibylConfig, Backend as BackendKind, BackendSelector } from "../config/schema.js"
import { defaultSpawn, type SpawnFn } from "./claude-cli.js"
import { BackendError, type Backend, type BackendRequest, type BackendResult } from "./types.js"

export type ProbeStatus =
  /** Present and usable. */
  | "available"
  /** It ran, and told us it is not signed in. A credential verdict. */
  | "unauthenticated"
  /** Not installed, or no credential configured. */
  | "absent"
  /**
   * ⛔ The probe did not RUN — spawn failure, timeout, or output we cannot interpret. This is a
   * statement about the probe, NEVER about the credential.
   */
  | "probe_failed"

export interface ProbeCheck {
  readonly name: BackendKind
  readonly status: ProbeStatus
  /** Human-readable, and specific enough to act on. */
  readonly detail: string
  /** The literal output the verdict came from, truncated. Never a credential. */
  readonly evidence?: string
}

export interface Resolution {
  /** `none` runs the deterministic passes only — diff parse, pre-existing classification, path rules. */
  readonly backend: BackendKind | "none"
  /** ⛔ Always set, for every outcome including success. */
  readonly reason: string
  /**
   * Set when `on_unavailable = "fallback"` degraded the run. ⛔ Present means every finding must be
   * stamped with what actually ran; it is not a full review by the requested backend.
   */
  readonly degradedFrom?: BackendKind
  readonly checks: readonly ProbeCheck[]
}

export interface ProbeDeps {
  readonly spawn?: SpawnFn
  /**
   * Resolves a credential by NAME. ⚠️ Unset in `process.env` does not mean absent — config.md §3
   * lists the store and an OAuth profile after the environment — so this is injected rather than
   * being a hard-coded `process.env` read.
   */
  readonly lookupCredential?: (name: string) => string | undefined
  readonly env?: NodeJS.ProcessEnv
}

const LOGGED_OUT_TEXT = /not logged in|please run .{0,12}claude (auth )?login/i

/**
 * Probe the `claude` CLI.
 *
 * ⛔ Reads STDOUT. `claude auth status` emits JSON with a `loggedIn` boolean (measured 2026-09-15 on
 * 2.1.272), and the exit code is 0 either way. If the JSON is absent we fall back to the prose
 * marker; if neither is present the answer is `probe_failed`, not a guess.
 */
export async function probeClaudeCli(cfg: SibylConfig, deps: ProbeDeps = {}): Promise<ProbeCheck> {
  const spawn = deps.spawn ?? defaultSpawn
  const bin = cfg.backend.claudeCli.bin
  let out
  try {
    out = await spawn(bin, ["auth", "status", "--json"], {
      env: deps.env ?? process.env,
      timeoutMs: cfg.backend.probeTimeoutMs,
    })
  } catch (e) {
    return {
      name: "claude_cli",
      status: "probe_failed",
      detail: `the auth probe threw before producing output: ${(e as Error).message}`,
    }
  }

  if (out.spawnError !== undefined) {
    const missing = /ENOENT/.test(out.spawnError.message)
    return {
      name: "claude_cli",
      status: missing ? "absent" : "probe_failed",
      detail: missing
        ? `\`${bin}\` is not on PATH`
        : `the auth probe could not be run: ${out.spawnError.message}`,
      evidence: out.stderr.slice(0, 200),
    }
  }

  const combined = `${out.stdout}\n${out.stderr}`
  try {
    const d = JSON.parse(out.stdout) as { loggedIn?: unknown; authMethod?: unknown; subscriptionType?: unknown }
    if (typeof d.loggedIn !== "boolean") {
      return {
        name: "claude_cli",
        status: "probe_failed",
        detail: `\`${bin} auth status --json\` returned JSON with no loggedIn field; this build's probe output is not one we understand`,
        evidence: out.stdout.slice(0, 200),
      }
    }
    if (!d.loggedIn) {
      return {
        name: "claude_cli",
        status: "unauthenticated",
        detail: `\`${bin}\` is installed but not signed in — a background process often cannot read the login keychain; \`claude setup-token\` is the fix`,
      }
    }
    const how = typeof d.authMethod === "string" ? d.authMethod : "unknown method"
    const plan = typeof d.subscriptionType === "string" ? `, ${d.subscriptionType} subscription` : ""
    return { name: "claude_cli", status: "available", detail: `signed in via ${how}${plan}` }
  } catch {
    // Not JSON. ⚠️ The prose form still has to be understood, because the exit code will be 0.
    if (LOGGED_OUT_TEXT.test(combined)) {
      return {
        name: "claude_cli",
        status: "unauthenticated",
        detail: `\`${bin}\` printed a signed-out message on stdout and exited ${out.code} — the exit code is not evidence here`,
        evidence: out.stdout.slice(0, 200),
      }
    }
    return {
      name: "claude_cli",
      status: "probe_failed",
      detail: `\`${bin} auth status --json\` produced output we cannot interpret (exit ${out.code}); this is a failed probe, not a rejected credential`,
      evidence: out.stdout.slice(0, 200),
    }
  }
}

/**
 * Probe the API credential.
 *
 * ⚠️ PRESENCE ONLY. No request is made, so `available` means "a credential is configured", not "a
 * credential works". Saying otherwise would be a claim we did not observe — and validating it would
 * spend money and time on every run. The reason string says which of the two it is.
 */
export function probeAnthropicApi(cfg: SibylConfig, deps: ProbeDeps = {}): ProbeCheck {
  const name = cfg.backend.anthropicApi.apiKeyName
  const lookup = deps.lookupCredential ?? ((n: string) => (deps.env ?? process.env)[n])
  const value = lookup(name)
  if (value === undefined || value === "") {
    return { name: "anthropic_api", status: "absent", detail: `no credential found under the name ${name}` }
  }
  // ⛔ Presence and last four only. config.md §5: a credential may be SET but never READ BACK.
  return {
    name: "anthropic_api",
    status: "available",
    detail: `a credential is configured under ${name} (…${value.slice(-4)}); not validated — no request was made`,
  }
}

/**
 * Resolve `[backend].default` into the backend that will actually run, with the reason.
 *
 * Order follows config.md §3 exactly: authed CLI → `claude_cli`; CLI present but unauthed with a key
 * present → `anthropic_api` **with the reason printed**; neither → `none`, naming what to set up.
 */
export async function resolveBackend(cfg: SibylConfig, deps: ProbeDeps = {}): Promise<Resolution> {
  const selector: BackendSelector = cfg.backend.default

  if (selector === "none") {
    return {
      backend: "none",
      reason: "[backend].default is none — the deterministic passes will run and no model will be called",
      checks: [],
    }
  }

  const cli = await probeClaudeCli(cfg, deps)
  const api = probeAnthropicApi(cfg, deps)
  const checks = [cli, api]
  const statusOf = (k: BackendKind): ProbeCheck => (k === "claude_cli" ? cli : api)

  if (selector === "auto") {
    if (cli.status === "available") {
      return { backend: "claude_cli", reason: `auto → claude_cli: ${cli.detail}`, checks }
    }
    if (api.status === "available") {
      return {
        backend: "anthropic_api",
        reason: `auto → anthropic_api: the claude CLI is unusable (${cli.detail}), and ${api.detail}`,
        checks,
      }
    }
    return {
      backend: "none",
      reason:
        `auto → none: neither backend is usable. claude CLI — ${cli.detail}. API — ${api.detail}. ` +
        `Sign in with \`claude setup-token\`, or set ${cfg.backend.anthropicApi.apiKeyName}.`,
      checks,
    }
  }

  // An explicit selection. It is still probed, because "I asked for it" is not availability.
  const wanted: BackendKind = selector
  const check = statusOf(wanted)
  if (check.status === "available") {
    return { backend: wanted, reason: `[backend].default is ${wanted}: ${check.detail}`, checks }
  }

  switch (cfg.backend.onUnavailable) {
    case "fail":
      // ⛔ Names the probe that failed and its output — config.md §6, exit 69.
      throw new BackendError(
        `[backend].default is ${wanted} and it is not available: ${check.detail}. ` +
          `on_unavailable is "fail", so the run stops rather than quietly using a weaker brain.`,
        wanted,
        check.evidence ?? "",
      )
    case "skip":
      return {
        backend: "none",
        reason: `${wanted} is unavailable (${check.detail}) and on_unavailable is "skip": the deterministic passes will run and no model will be called`,
        checks,
      }
    case "fallback": {
      for (const candidate of cfg.backend.fallbackOrder) {
        if (candidate === wanted) continue
        const alt = statusOf(candidate)
        if (alt.status !== "available") continue
        // ⛔ degradedFrom is the whole point of this branch. A fallback review is stamped, never
        // presented as a full review by the requested backend.
        return {
          backend: candidate,
          degradedFrom: wanted,
          reason: `DEGRADED: ${wanted} is unavailable (${check.detail}); fell back to ${candidate} (${alt.detail}). Every finding is stamped with ${candidate}.`,
          checks,
        }
      }
      return {
        backend: "none",
        reason: `${wanted} is unavailable (${check.detail}) and no backend in fallback_order is usable either`,
        checks,
      }
    }
  }
}

/**
 * Wrap a backend so every result it returns carries `degradedFrom`.
 *
 * ⛔ This exists so the stamp cannot be forgotten at a call site. spec/config.md: *"never silently
 * degrade to a weaker brain and present the result as a full review."* A convention that every
 * caller must remember is one that eventually is not.
 */
export function stampDegradation(inner: Backend, degradedFrom: BackendKind): Backend {
  return {
    kind: inner.kind,
    async run(req: BackendRequest): Promise<BackendResult> {
      const r = await inner.run(req)
      return { ...r, degradedFrom }
    },
  }
}

/** One line for the resolution banner. Always says why, including on the happy path. */
export function bannerLine(r: Resolution): string {
  return `backend: ${r.backend}${r.degradedFrom === undefined ? "" : ` (degraded from ${r.degradedFrom})`} — ${r.reason}`
}
